import { useCallback } from 'react';
import { trackAnalyticsEvent } from 'analytics';
import {
	LaunchAction,
	Session,
	useResourceManagerContext,
} from 'App/Activity/ResourceManagerContext';
import { useResourceActions } from 'App/Activity/useResourceActions';
import { RemediationActions } from 'App/ProgressAlert/utils';
import { refreshAllResources } from 'Environment/callbacks';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { TroubleshootFixStatus } from 'Workspace/ResourceProvider/Context';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { TroubleshootEventPayload } from 'Workspace/TelemetryEvents/troubleshoot/troubleshootCASReporter';

const isActionImplemented = (action: string): boolean => {
	switch (action) {
		case RemediationActions.RESTART:
		case RemediationActions.REFRESH_RESOURCE_LIST:
			return true;
		default:
			return false;
	}
};

interface RemediationActionResolver {
	remediationFunction: (() => void) | null;
	fixStatus: TroubleshootFixStatus;
}

export const useRemediationActionResolver = (
	resource: Resource,
	action: string,
	transactionId: string,
	errorMessage: string,
	onClose: (operationId: string) => void
): RemediationActionResolver => {
	const resourceActions = useResourceActions(resource);
	const { onDesktopAction } = useResourceManagerContext();

	const { value } = useLoadableResourceContext();
	const { resourceLatestStatus, updateResourceTroubleshootFixStatus } = value || {};
	const latestFixStatus = resourceLatestStatus?.[resource.id]?.troubleshootFixStatus;
	const { id: resourceId, name: resourceName, iconurl: resourceIconUrl } = resource;

	const executeAction = useCallback(() => {
		trackAnalyticsEvent(
			TroubleshootEventPayload.fixIsssue({
				transactionId,
				resourceId,
				resourceName,
				errorMessage,
				remediationAction: action,
			})
		);
		onClose(transactionId);
		switch (action) {
			case RemediationActions.RESTART:
				const session = {
					applications: [
						{
							id: resourceId,
							resource,
							name: resourceName,
							iconUrl: resourceIconUrl,
						},
					],
					machineData: resourceActions?.machine,
				} as Session;

				onDesktopAction(LaunchAction.RESTART, session, true);
				break;

			case RemediationActions.REFRESH_RESOURCE_LIST:
				refreshAllResources();
				updateResourceTroubleshootFixStatus?.(
					resourceId,
					TroubleshootFixStatus.SUCCEEDED
				);
				break;
		}
	}, [
		action,
		onClose,
		transactionId,
		resourceId,
		errorMessage,
		resource,
		resourceName,
		resourceIconUrl,
		resourceActions?.machine,
		onDesktopAction,
		updateResourceTroubleshootFixStatus,
	]);

	const remediationFunction =
		resourceLatestStatus?.[resourceId]?.troubleshootFixStatus ===
			TroubleshootFixStatus.NOT_STARTED && isActionImplemented(action)
			? executeAction
			: null;

	return {
		remediationFunction,
		fixStatus: latestFixStatus,
	};
};
